// hooks/usePasteUpload.js
// ─── Ctrl+V (clipboard) → File upload köprüsü ─────────────────────────────────
// Tek sorumluluk: pano (clipboard) içinde *dosya* varsa onFiles(files)'i çağır.
// - Yalnızca `kind === 'file'` item'ları toplar → metin kopyala/yapıştır akışı
//   bozulmaz (input/textarea içinde yazı yapıştırma etkilenmez).
// - `enabled` flag'i ile aktif olduğu pencere (örn. modal açıkken) kapsamı
//   sınırlanır → birden çok upload alanı aynı anda dinlemez.
// - Opsiyonel `accept` (HTML accept attribute formatı: "image/*,.pdf,.xls") ile
//   istenmeyen tipler düşürülür.
// - Ekran görüntüsü (PrtSc → Ctrl+V) tipik olarak `image/png` MIME ile, name=""
//   olarak gelir; biz adı `pasted-<ts>.png` olarak set ediyoruz ki backend ve UI
//   listesinde anlamlı görünsün.

import { useEffect, useRef } from 'react';

function matchesAccept(file, accept) {
  if (!accept) return true;
  const tokens = accept.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
  if (tokens.length === 0) return true;
  const name = (file.name || '').toLowerCase();
  const mime = (file.type || '').toLowerCase();
  return tokens.some((tok) => {
    if (tok.startsWith('.')) return name.endsWith(tok);
    if (tok.endsWith('/*')) return mime.startsWith(tok.slice(0, -1));
    return mime === tok;
  });
}

/**
 * @param {(files: File[]) => void | Promise<void>} onFiles  Pasted file handler.
 * @param {object} opts
 *  - enabled: boolean — false ise listener takılmaz (varsayılan true).
 *  - accept: string|null — HTML accept formatı; eşleşmeyen dosyalar atılır.
 *  - target: React ref (HTMLElement) — verilirse o node'a, yoksa window'a takılır.
 *  - ignoreEditable: boolean — true (default) → metin alanlarına focus iken yoksay.
 */
export default function usePasteUpload(onFiles, opts = {}) {
  const { enabled = true, accept = null, target = null, ignoreEditable = true } = opts;
  // onFiles referansını ref'te tut, listener dependency'sini sabitlemek için.
  const cbRef = useRef(onFiles);
  useEffect(() => { cbRef.current = onFiles; }, [onFiles]);

  useEffect(() => {
    if (!enabled) return undefined;
    const node = (target && target.current) || window;

    const handler = (e) => {
      // Kullanıcı bir input/textarea/contenteditable'a yazı yapıştırıyorsa
      // dokunma. (Sadece dosya pano içeriği bizi ilgilendiriyor; yine de
      // bu guard, klavye odağı bir text input'taysa görüntü yapıştırılmaması
      // gibi nadir senaryoyu da netleştirir.)
      if (ignoreEditable) {
        const ae = document.activeElement;
        if (ae) {
          const tag = (ae.tagName || '').toLowerCase();
          if (tag === 'input' || tag === 'textarea' || ae.isContentEditable) {
            // Sadece içerikte dosya yoksa pas geç — dosya varsa yine de yakala
            // ki ekran görüntüsü vb. yine yüklenebilsin.
            const itemsPeek = e.clipboardData?.items || [];
            let hasFile = false;
            for (const it of itemsPeek) {
              if (it.kind === 'file') { hasFile = true; break; }
            }
            if (!hasFile) return;
          }
        }
      }

      const items = e.clipboardData?.items;
      if (!items || items.length === 0) return;
      const files = [];
      const ts = Date.now();
      let imgCounter = 0;
      for (const item of items) {
        if (item.kind !== 'file') continue;
        const f = item.getAsFile();
        if (!f) continue;
        // Ekran görüntülerinde name boş gelebilir — anlamlı bir isim ver.
        let finalFile = f;
        if (!f.name || f.name === 'image.png' || f.name === '') {
          const ext = (f.type && f.type.split('/')[1]) || 'bin';
          const newName = `pasted-${ts}${imgCounter ? '-' + imgCounter : ''}.${ext}`;
          try { finalFile = new File([f], newName, { type: f.type, lastModified: f.lastModified }); }
          catch { /* eski tarayıcı: orijinal File'ı bırak */ }
          imgCounter += 1;
        }
        if (matchesAccept(finalFile, accept)) files.push(finalFile);
      }
      if (files.length === 0) return;
      e.preventDefault();
      try { cbRef.current && cbRef.current(files); } catch { /* sessiz */ }
    };

    node.addEventListener('paste', handler);
    return () => node.removeEventListener('paste', handler);
  }, [enabled, accept, target, ignoreEditable]);
}
